const SHORT_DAY = new Intl.DateTimeFormat("en-US", {
  weekday: "short",
});
const SHORT_MONTH_DAY = new Intl.DateTimeFormat("en-US", {
  month: "short",
  day: "numeric",
});
const FULL = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
});

function parseIsoDate(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12)); // noon UTC to dodge tz wobble
}

export function fmtShortDay(iso: string): string {
  return SHORT_DAY.format(parseIsoDate(iso));
}

export function fmtMonthDay(iso: string): string {
  return SHORT_MONTH_DAY.format(parseIsoDate(iso));
}

export function fmtFullDate(iso: string): string {
  return FULL.format(parseIsoDate(iso));
}

export function fmtWeekLabel(weekStartIso: string): string {
  const start = parseIsoDate(weekStartIso);
  const end = new Date(start.getTime() + 6 * 86_400_000);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  if (sameMonth) {
    return `${SHORT_MONTH_DAY.format(start)} – ${end.getUTCDate()}`;
  }
  return `${SHORT_MONTH_DAY.format(start)} – ${SHORT_MONTH_DAY.format(end)}`;
}

export function fmtMinutes(n: number, opts: { compact?: boolean } = {}): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  if (opts.compact && rounded >= 1000) {
    return `${(rounded / 1000).toFixed(1)}k`;
  }
  return rounded.toLocaleString("en-US");
}

export function fmtNumber(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
}

export function fmtMiles(meters: number): string {
  if (!Number.isFinite(meters)) return "—";
  return (meters / 1609.344).toFixed(2);
}

export function fmtCalories(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return Math.round(n).toLocaleString("en-US");
}

export function fmtDurationMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export function fmtDurationSeconds(seconds: number): string {
  if (!Number.isFinite(seconds)) return "—";
  if (seconds <= 0) return "0m";
  return fmtDurationMinutes(seconds / 60);
}

export function fmtSignedDelta(n: number): string {
  if (!Number.isFinite(n)) return "—";
  const rounded = Math.round(n);
  if (rounded === 0) return "±0";
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

export function fmtRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "—";
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 5) return "just now";
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}

export function todayIsoInTimezone(timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date());
  const lookup: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") lookup[p.type] = p.value;
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
}
